import { WRITING_ALERT_STOPWORDS } from '@/constants/writingAlerts/lexicon'
import { normalizeWritingWord } from '@/utils/writingAlerts/normalize'

export const WORD_CHAR = /[A-Za-zÁÉÍÓÚÜÑáéíóúüñ]/
export const SENTENCE_END = /[.!?…\u2026\n\r]/

const STOPWORDS = new Set(WRITING_ALERT_STOPWORDS.map((word) => normalizeWritingWord(word)))

export type TermCount = { term: string; count: number }

export type TypingAccumulator = {
  pendingRaw: string
  sentence: string[]
}

export function createTypingAccumulator(): TypingAccumulator {
  return { pendingRaw: '', sentence: [] }
}

/** Shared with on-the-fly alerts: lowercase, no tildes, ñ kept. */
export function normalizeTerm(raw: string): string {
  return normalizeWritingWord(raw)
}

export function isStopword(term: string): boolean {
  return STOPWORDS.has(term)
}

export function isWordChar(char: string): boolean {
  return WORD_CHAR.test(char)
}

export function isSentenceEnd(char: string): boolean {
  return SENTENCE_END.test(char)
}

function increment(counts: Map<string, number>, term: string) {
  counts.set(term, (counts.get(term) || 0) + 1)
}

function emitNgrams(sentence: string[], counts: Map<string, number>) {
  const length = sentence.length
  for (let size = 2; size <= 4; size += 1) {
    if (length >= size) {
      const gramWords = sentence.slice(length - size)
      const first = gramWords[0]
      const last = gramWords[gramWords.length - 1]
      if (first && last && !isStopword(first) && !isStopword(last)) {
        increment(counts, gramWords.join(' '))
      }
    }
  }
}

/**
 * Count a word closed by space or punctuation. N-grams (2–4) stay inside the
 * current sentence. Lone stopwords are skipped; n-grams may include internal
 * stopwords but must not start or end with one.
 */
export function ingestClosedWord(
  acc: TypingAccumulator,
  rawWord: string,
  counts: Map<string, number>,
  wordsRef: { n: number },
) {
  acc.pendingRaw = ''
  const normalized = normalizeTerm(rawWord)
  if (!normalized) return
  wordsRef.n += 1
  acc.sentence.push(normalized)
  if (!isStopword(normalized)) {
    increment(counts, normalized)
  }
  emitNgrams(acc.sentence, counts)
}

export function ingestSentenceBreak(acc: TypingAccumulator) {
  acc.pendingRaw = ''
  acc.sentence = []
}

export function resetTypingAccumulator(acc: TypingAccumulator) {
  acc.pendingRaw = ''
  acc.sentence = []
}

export function topTerms(counts: Map<string, number>, limit: number): TermCount[] {
  return [...counts.entries()]
    .map(([term, count]) => ({ term, count }))
    .sort((a, b) => b.count - a.count || a.term.localeCompare(b.term))
    .slice(0, limit)
}
